import dayjs from "dayjs";
import {
  EntityChangeAction,
  IEntityChangeLogListItem,
  IEntityChangeReference
} from "../../interfaces/endpoints/IEntityChangeLog.types";
import { formatUtc } from "./changeHistoryTime";
import {
  COLUMN_LABELS,
  DAY_NAMES,
  DAY_NAMES_SHORT,
  ENTITY_COLUMN_LABELS,
  ENTITY_LABELS,
  ENUM_VALUE_LABELS,
  HIDDEN_COLUMNS,
  LINK_COLUMNS
} from "./changeHistoryDictionary";

export const ACTION_LABELS: Record<EntityChangeAction, string> = {
  Created: "U krijua",
  Updated: "U ndryshua",
  SoftDeleted: "U fshi",
  Restored: "U rikthye",
  HardDeleted: "U fshi përfundimisht"
};

export const ACTION_STYLES: Record<EntityChangeAction, string> = {
  Created: "bg-green-100 text-green-800",
  Updated: "bg-blue-100 text-blue-800",
  SoftDeleted: "bg-orange-100 text-orange-800",
  Restored: "bg-teal-100 text-teal-800",
  HardDeleted: "bg-red-100 text-red-800"
};

// A row written by a revert reads as the undo it is, whatever the write technically was.
export const REVERT_LABEL = "U kthye mbrapa";
export const REVERT_STYLE = "bg-purple-100 text-purple-800";

type ActionRow = Pick<IEntityChangeLogListItem, "action" | "revertOf">;

export const actionLabel = (row: ActionRow): string => (row.revertOf ? REVERT_LABEL : ACTION_LABELS[row.action]);

export const actionStyle = (row: ActionRow): string => (row.revertOf ? REVERT_STYLE : ACTION_STYLES[row.action]);

// "kthen ndryshimin e 16.09.2026 14:24 (U ndryshua)": what an undo row undid.
export const describeRevertOf = (row: ActionRow): string | null =>
  row.revertOf
    ? `kthen ndryshimin e ${formatUtc(row.revertOf.changedAtUtc)} (${ACTION_LABELS[row.revertOf.action]})`
    : null;

// The other side of the same story: this change was undone later, and when.
export const describeRevertedBy = (row: Pick<IEntityChangeLogListItem, "revertedBy">): string | null =>
  row.revertedBy ? `U kthye mbrapa më ${formatUtc(row.revertedBy.changedAtUtc)}` : null;

// Entity names offered in the search box, with their plain-language label. Any other CLR name works too.
export const COMMON_ENTITY_NAMES = Object.keys(ENTITY_LABELS);

// "OperationalIntervalId" -> "Operational Interval", the last resort when no label is known.
const humanize = (column: string): string =>
  column.replace(/Id$/, "").replace(/([a-z])([A-Z])/g, "$1 $2").trim();

export const labelForEntity = (entityName: string): string => ENTITY_LABELS[entityName] ?? entityName;

export const labelForColumn = (entityName: string, column: string): string =>
  ENTITY_COLUMN_LABELS[entityName]?.[column] ?? COLUMN_LABELS[column] ?? humanize(column);

export const isHiddenColumn = (column: string): boolean => HIDDEN_COLUMNS.has(column);

// Who made the change, for a table cell.
export const describeActor = (row: IEntityChangeLogListItem): string => {
  if (row.actorName) return row.actorName;
  switch (row.actorType) {
    case "Background":
      return "Sistemi";
    case "Customer":
      return row.actorId ? `Klienti #${row.actorId}` : "Klienti";
    case "Admin":
      return row.actorId ? `Admin #${row.actorId}` : "Admin";
    default:
      return "Anonim";
  }
};

export { parseChanges, parseState, prettyJson } from "./changeHistoryJson";

export const formatDays = (days: number[], compact = false): string =>
  days.length === 7
    ? "Çdo ditë"
    : days.map((d) => (compact ? DAY_NAMES_SHORT[d] : DAY_NAMES[d]) ?? String(d)).join(", ");

const TIME_ONLY = /^\d{2}:\d{2}:\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// A stored value as a reader should see it: names for ids, weekday names for intervals, Po/Jo for
// flags, local dates, "—" for nothing.
export const formatDisplayValue = (
  entityName: string,
  column: string,
  value: unknown,
  references: IEntityChangeReference[],
  // In a table cell: short weekday names, so the value that changed is not pushed out of sight.
  compact = false
): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Po" : "Jo";
  if (typeof value === "number") {
    const enumLabel = ENUM_VALUE_LABELS[`${entityName}.${column}`]?.[value];
    if (enumLabel) return enumLabel;
    const reference = references.find((r) => r.column === column && r.id === value);
    if (reference) {
      if (reference.days && reference.days.length) return formatDays(reference.days, compact);
      if (reference.display) return reference.display;
    }
    // A foreign key of zero is an empty link, not row number zero: "#0" reads as a row that exists.
    if (value === 0 && (column.endsWith("Id") || LINK_COLUMNS.has(column))) return "—";
    return column.endsWith("Id") ? `#${value}` : String(value);
  }
  if (typeof value === "string") {
    const enumLabel = ENUM_VALUE_LABELS[`${entityName}.${column}`]?.[value];
    if (enumLabel) return enumLabel;
    if (TIME_ONLY.test(value)) return value.slice(0, 5);
    if (ISO_DATE_TIME.test(value)) return dayjs(value).format("DD.MM.YYYY HH:mm");
    return value;
  }
  return JSON.stringify(value);
};

export { formatUtc, formatUtcDate, formatUtcTime } from "./changeHistoryTime";
export { revertParagraph } from "./revertText";
